import { debounce } from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { customerSelector } from '@/api/v1';
import { VerifyModalStatusIndicatorItem } from '@/components/molecules/VerifyModalStatusIndicatorItem';
import { useTranslator } from '@/hooks/useTranslator';
import { Customer, CustomerDocuments, EntityType, Owner } from '@/types/customer';
import { Steps } from '@/types/kyb';
import { ChildStep, ParentStep, Status } from '@/types/steps';

interface Props {
  readonly open: boolean;
  readonly statuses: Record<string, Status>;
  readonly steps: readonly ParentStep<unknown>[];

  onClose(): void;
}

interface CompletionIndicator<T extends Customer | Record<string, Status>> {
  readonly label: string;
  readonly getStatus: (parameter?: T) => Status;
}

export const VerifyBusinessModal: React.FC<Props> = (props: Props): React.ReactElement => {
  const { data: customer } = useSelector(customerSelector);
  const { open, statuses, steps, onClose } = props;

  const [container, setContainer] = React.useState<HTMLDivElement | null>(null);
  const [containerWrapperStyle, setContainerWrapperStyle] = React.useState<SystemStyleObject>({});

  const tr = useTranslator();

  const recomputeStyle = React.useCallback((): void => {
    if (!container) {
      return;
    }

    const overflows = container.scrollHeight > container.clientHeight;
    if (overflows) {
      setContainerWrapperStyle(styles.containerWithOverflow);
    } else {
      setContainerWrapperStyle(styles.containerWithoutOverflow);
    }
  }, [container]);

  React.useEffect((): VoidFunction | void => {
    if (!container) {
      return;
    }

    const observer = new ResizeObserver(debounce(recomputeStyle, 40));
    if (container.parentElement instanceof Element) {
      observer.observe(container.parentElement);
    }

    return (): void => {
      observer.disconnect();
    };
  }, [container, recomputeStyle]);

  const mappedStatuses = React.useMemo((): Record<string, Status> => {
    // Don't do any calculations if not open -\_o_/-
    if (!open) {
      return {};
    }

    return steps.reduce(
      (
        parentStatuses: Record<string, Status>,
        step: ParentStep<unknown>,
      ): Record<string, Status> => {
        const { children } = step;
        const childrenCompleted = children.every((childStep: ChildStep<unknown>): boolean => {
          return (statuses[childStep.type] & Status.completed) === Status.completed;
        });

        if (childrenCompleted) {
          return { ...parentStatuses, [step.type]: Status.completed };
        } else {
          return { ...parentStatuses, [step.type]: Status.initial };
        }
      },
      {},
    );
  }, [open, statuses, steps]);

  if (!open) {
    return <></>;
  }

  return ReactDOM.createPortal(
    <>
      <Backdrop open={true} />
      <Box aria-hidden="false" ref={setContainer} sx={containerWrapperStyle}>
        <Box height={32} />
        <Paper sx={styles.container}>
          <Typography variant="h5">{tr('FOUR_STEPS_BEFORE_FIRST_PAYMENT')}</Typography>
          <Box height={24} />
          <Typography variant="body2" color="secondary">
            {tr(
              'We only need a few more details about your business and some documents to verify your company before using Caliza.',
            )}
          </Typography>

          <Box my={5}>
            <Paper sx={styles.paper}>
              <Box display="flex" gap={3}>
                <Box display="flex" flexDirection="column" width={256} gap={2}>
                  <Typography variant="subtitle1">Share business details</Typography>
                  {shareBusinessDetails.map(
                    (
                      indicator: CompletionIndicator<Record<string, Status>>,
                    ): React.ReactElement => (
                      <VerifyModalStatusIndicatorItem
                        key={indicator.label}
                        status={indicator.getStatus(mappedStatuses)}>
                        {tr(indicator.label)}
                      </VerifyModalStatusIndicatorItem>
                    ),
                  )}
                </Box>

                <Box display="flex" flexDirection="column" width={256} gap={2}>
                  <Typography variant="subtitle1">Upload documents</Typography>
                  {uploadDocuments.map(
                    (indicator: CompletionIndicator<Customer>): React.ReactElement => {
                      return (
                        <VerifyModalStatusIndicatorItem
                          key={indicator.label}
                          status={indicator.getStatus(customer)}>
                          {tr(indicator.label)}
                        </VerifyModalStatusIndicatorItem>
                      );
                    },
                  )}
                </Box>
              </Box>
            </Paper>
          </Box>

          <Button variant="contained" color="primary" onClick={onClose} fullWidth={true}>
            Start
          </Button>
          <Box mt={4} textAlign="center">
            <Link to="/dashboard">
              <Typography fontSize={16} fontWeight={400} color="text.primary">
                Back to home
              </Typography>
            </Link>
          </Box>
        </Paper>
        <Box height={32} />
      </Box>
    </>,
    document.body,
  );
};

const styles: Record<string, SystemStyleObject> = {
  containerWithOverflow: {
    position: 'fixed',
    overflow: 'auto',
    inset: 0,
  },
  containerWithoutOverflow: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    width: 744,
    height: 777,
    mx: 'auto',
    flexShrink: 0,
    backgroundColor: 'custom.White',
    borderBottomLeftRadius: {
      sm: 24,
      xs: 0,
    },
    borderBottomRightRadius: {
      sm: 24,
      xs: 0,
    },
    py: 8,
    px: {
      sm: 8,
      xs: 3,
    },
  },
  paper: {
    padding: 4,
  },
};

const shareBusinessDetails: readonly CompletionIndicator<Record<string, Status>>[] = [
  {
    label: 'Business information',
    getStatus: (parameter?: Record<string, Status>): Status => {
      return parameter?.[Steps.businessInformation] ?? Status.initial;
    },
  },
  {
    label: 'Business operations',
    getStatus: (parameter?: Record<string, Status>): Status => {
      return parameter?.[Steps.businessOperations] ?? Status.initial;
    },
  },
  {
    label: 'Organization',
    getStatus: (parameter?: Record<string, Status>): Status => {
      return parameter?.[Steps.organization] ?? Status.initial;
    },
  },
];

const hasDocument = (customer: Customer | undefined, name: keyof CustomerDocuments): boolean => {
  const documents = customer?.businessInfo?.documents;
  if (!documents) {
    return false;
  }

  return !!documents[name];
};

const atLeastOneOwnerCompleted = (customer?: Customer): boolean => {
  const entityType = customer?.businessInfo?.entityType;
  if (
    entityType === EntityType.empresaIndividualEI ||
    entityType === EntityType.microEmpreendedorIndividualMEI
  ) {
    return true;
  }

  const owners = customer?.businessInfo?.organization?.owners;
  if (!owners || owners.length === 0) {
    return false;
  }

  return owners.every((owner: Owner): boolean => owner.document !== null && owner.document !== '');
};

const uploadDocuments: readonly CompletionIndicator<Customer>[] = [
  {
    label: 'Incorporation document',
    getStatus: (customer?: Customer): Status =>
      hasDocument(customer, 'incorporation') ? Status.completed : Status.initial,
  },
  {
    label: 'CNPJ registration',
    getStatus: (customer?: Customer): Status =>
      hasDocument(customer, 'cnpjRegistration') ? Status.completed : Status.initial,
  },
  {
    label: 'Power of attorney',
    getStatus: (customer?: Customer): Status =>
      hasDocument(customer, 'powerOfAttorney') ? Status.completed : Status.initial,
  },
  {
    label: 'Business bank account statement',
    getStatus: (customer?: Customer): Status =>
      hasDocument(customer, 'bankAccountStatement') ? Status.completed : Status.initial,
  },
  {
    label: 'IDs of legal representative and beneficial owners',
    getStatus: (customer?: Customer): Status =>
      atLeastOneOwnerCompleted(customer) ? Status.completed : Status.initial,
  },
];
